// ABOUTME: Maps a completed TechLog sector onto the Skylegs post-flight payload pushed to an existing flight.
import { TechlogToSkylegsFlightInput } from './validation';

/**
 * Flight category pinned on every pushed flight. The statement of work fixes it
 * at `NCC`, which exists on no kind on the tenant, so the mapping is pinned to a
 * category that does exist and Japat will be asked for the intended one. The
 * name is the constant rather than the id because a name is reviewable and
 * fails loudly, while a stale numeric id resolves silently to the wrong
 * category.
 */
const SKYLEGS_FLIGHT_CATEGORY_NAME = 'Private';

/** Only `kind: "MissionFlight"` categories are flight categories. */
const SKYLEGS_FLIGHT_CATEGORY_KIND = 'MissionFlight';

/** Skylegs MissionFlight fuel masses are lbs, vendor-documented and UI-verified. */
const POUNDS_PER_KILOGRAM = 2.20462262185;

/**
 * Skylegs oil slots hold quarts. TechLog records oil in `ml` or `qt`, so a
 * millilitre figure converts exactly on the US liquid quart aviation oil is
 * measured in.
 */
const MILLILITRES_PER_QUART = 946.352946;

export const techlogFlightToSkylegsSchema = {
  category_id: (input: TechlogToSkylegsFlightInput) => {
    // Matching on name alone is unsafe: `Private-91` and `Private-91k` are
    // `kind: "Aircraft"` categories, so a name-only lookup lands on an aircraft
    // category id that does not belong on a flight.
    const category = input.skylegs.categories.find(
      (candidate) =>
        candidate.kind === SKYLEGS_FLIGHT_CATEGORY_KIND &&
        candidate.name === SKYLEGS_FLIGHT_CATEGORY_NAME,
    );

    if (!category) {
      throw new Error(
        `Skylegs has no ${SKYLEGS_FLIGHT_CATEGORY_KIND} category named "${SKYLEGS_FLIGHT_CATEGORY_NAME}"; a tenant administrator has to create it before flights can be pushed.`,
      );
    }

    return category.id;
  },

  actual_obt: (input: TechlogToSkylegsFlightInput) =>
    input.techlog.sector.offblocksTime
      ? new Date(input.techlog.sector.offblocksTime).toISOString()
      : undefined,

  actual_tot: (input: TechlogToSkylegsFlightInput) =>
    input.techlog.sector.takeoffTime
      ? new Date(input.techlog.sector.takeoffTime).toISOString()
      : undefined,

  actual_ldt: (input: TechlogToSkylegsFlightInput) =>
    input.techlog.sector.landingTime
      ? new Date(input.techlog.sector.landingTime).toISOString()
      : undefined,

  actual_ibt: (input: TechlogToSkylegsFlightInput) =>
    input.techlog.sector.onblocksTime
      ? new Date(input.techlog.sector.onblocksTime).toISOString()
      : undefined,

  cruise_level: (input: TechlogToSkylegsFlightInput) => {
    // Japat record the cruise level in a post-flight custom field while the
    // standard `maxFlightLevel` stays null, so the custom field is read first.
    const custom = input.techlog.sector.postflightCustomData?.max_alt;
    const value =
      typeof custom === 'number' ? custom : input.techlog.sector.maxFlightLevel;

    // The unit of the custom field is unconfirmed: the observed 56 cannot be a
    // flight level for a Falcon 7X, so it may be hundreds of feet, thousands, or
    // metres. The integer is therefore passed through unchanged — any multiplier
    // would be a guess written into an aircraft record. A non-integer is dropped
    // rather than rounded, because Skylegs rejects a non-integer cruise level.
    return typeof value === 'number' && Number.isInteger(value)
      ? value
      : undefined;
  },

  remarks: (input: TechlogToSkylegsFlightInput) => {
    const custom = input.techlog.sector.postflightCustomData?.post_rem;
    const value =
      typeof custom === 'string' && custom.trim()
        ? custom
        : input.techlog.sector.remarks;

    if (!value) {
      return undefined;
    }

    // Skylegs rejects remarks over 500 characters and cannot store more.
    return value.slice(0, 500);
  },

  actual_pax: (input: TechlogToSkylegsFlightInput) => {
    const sector = input.techlog.sector;
    const counts = [
      sector.passengersMale,
      sector.passengersFemale,
      sector.passengersChildren,
      sector.passengersInfants,
    ].filter((count): count is number => typeof count === 'number');

    // The categories the sector records are summed, and a category left null
    // counts as none: the post-flight form is filled in only for the categories
    // the flight carried, so two male and the rest null is a two-passenger
    // flight, not an incomplete figure. No category at all is the different case
    // — TechLog has nothing to report, which is not a recorded zero — and leaves
    // Skylegs' own count untouched.
    return counts.length
      ? counts.reduce((total, count) => total + count, 0)
      : undefined;
  },

  // Skylegs holds two columns per counter: a cumulative running total
  // (`landings`, `cycles`, `apu_cycles`) carried forward from the aircraft's
  // previous flight, and the per-flight increment (`*_delta`). TechLog records
  // what happened on the sector, so its figures are increments.
  //
  // The totals are left alone. Skylegs computes them in its own post-flight form
  // and the API does not derive them from the increment, so writing TechLog's
  // cumulative counters there would replace the operator's counter reading with
  // a figure on a different baseline. A total that preserves the operator's
  // lineage needs the previous flight's totals, which no granted endpoint
  // supplies. `cycles_special` and `cycles_special2` are derived by Skylegs from
  // these increments and must never be sent either.
  landings_delta: (input: TechlogToSkylegsFlightInput) =>
    input.techlog.sector.landingsCount ?? undefined,

  cycles_delta: (input: TechlogToSkylegsFlightInput) =>
    input.techlog.sector.cycles?.airframe ?? undefined,

  apu_cycles_delta: (input: TechlogToSkylegsFlightInput) =>
    input.techlog.sector.cycles?.apu ?? undefined,

  // Airframe hobbs meter readings. Both systems hold decimal hours, so the
  // reading transfers unchanged; Skylegs renders 102.33 as 102:20.
  clock_start: (input: TechlogToSkylegsFlightInput) =>
    input.techlog.sector.hobbsDeparture ?? undefined,

  clock_end: (input: TechlogToSkylegsFlightInput) =>
    input.techlog.sector.hobbsArrival ?? undefined,

  // APU hour meter, as the pair of endpoints Skylegs holds it in: it has no
  // APU-hours increment field, so the sector's APU time can only be expressed as
  // the distance between two readings. TechLog holds both figures in seconds —
  // the audit log of SS-1179 shows `apuReading` moving 9378000 → 9392400 for a
  // 2605:00 → 2609:00 correction, and `src/activities/techlog.ts` renders it to
  // Leon's `totalApuHours` as `reading / 3600` — while Skylegs clocks are the
  // decimal hours the airframe pair above is already in.
  //
  // The reading is taken as the arrival meter, mirroring `hobbsArrival`. Which
  // end of the sector TechLog stamps it at is not established: if it is in fact
  // the departure meter, both endpoints sit one sector's APU time too high.
  apu_clock_start: (input: TechlogToSkylegsFlightInput) => {
    const { apuReading, apuSeconds } = input.techlog.sector;
    if (typeof apuReading !== 'number') {
      return undefined;
    }

    // A reading with no APU time has no second endpoint to pair it with, and one
    // endpoint of a meter pair would have Skylegs read an APU duration running
    // from whatever it already holds — the same harm the cumulative counters,
    // `actual_pax` and the all-zero fuel summary are withheld for.
    if (typeof apuSeconds !== 'number') {
      console.warn(
        `Skylegs apu_clock_start left unset on sector ${input.techlog.sector.id}: TechLog holds an APU meter reading but no APU running time to place the other end of the pair.`,
      );
      return undefined;
    }

    return Math.round(((apuReading - apuSeconds) / 3600) * 100) / 100;
  },

  apu_clock_end: (input: TechlogToSkylegsFlightInput) => {
    const { apuReading, apuSeconds } = input.techlog.sector;
    if (typeof apuReading !== 'number') {
      return undefined;
    }

    if (typeof apuSeconds !== 'number') {
      console.warn(
        `Skylegs apu_clock_end left unset on sector ${input.techlog.sector.id}: TechLog holds an APU meter reading but no APU running time to place the other end of the pair.`,
      );
      return undefined;
    }

    return Math.round((apuReading / 3600) * 100) / 100;
  },

  atl_number: (input: TechlogToSkylegsFlightInput) => {
    // The operator-facing trip number is the tech log reference; the sector
    // reference number is what TechLog carries when no trip number was assigned.
    const trip = input.techlog.trip;
    if (trip.number != null) {
      return String(trip.number);
    }
    if (trip.srpNumber != null) {
      return String(trip.srpNumber);
    }
    return undefined;
  },

  actual_fuel: (input: TechlogToSkylegsFlightInput) => {
    const summary = input.techlog.fuelSummary;
    if (!summary) {
      return undefined;
    }

    // A sector that holds no fuel records still answers the summary endpoint,
    // with zeros throughout. That is TechLog reporting nothing rather than
    // reporting an empty aircraft, and sending it would replace the operator's
    // own figures in Skylegs with a fuel state nobody recorded.
    if (
      !summary.preUpliftFuel &&
      !summary.postUpliftFuel &&
      !summary.usedFuel &&
      !summary.remainingFuel
    ) {
      return undefined;
    }

    // TechLog reports the summary in the aircraft's configured fuel unit. Fuel on
    // board given as a volume cannot be turned into the mass Skylegs holds: the
    // tanks carry fuel from earlier uplifts at densities nobody recorded, so any
    // figure would be a wrong mass on an aircraft record. The field is dropped
    // instead, and the merging PUT leaves Skylegs' own figure alone.
    const unit = input.techlog.aircraft.fuelUnit?.toLowerCase();
    if (unit === 'lb') {
      return summary.preUpliftFuel;
    }
    if (unit === 'kg') {
      return (
        Math.round(summary.preUpliftFuel * POUNDS_PER_KILOGRAM * 100) / 100
      );
    }
    console.warn(
      `Skylegs actual_fuel left unset on sector ${input.techlog.sector.id}: TechLog fuel unit "${input.techlog.aircraft.fuelUnit}" on aircraft ${input.techlog.aircraft.registration} is not a mass Skylegs can hold.`,
    );
    return undefined;
  },

  actual_fuel_start: (input: TechlogToSkylegsFlightInput) => {
    const summary = input.techlog.fuelSummary;
    if (!summary) {
      return undefined;
    }

    // Zeros throughout are a sector with no fuel records, not an empty aircraft.
    if (
      !summary.preUpliftFuel &&
      !summary.postUpliftFuel &&
      !summary.usedFuel &&
      !summary.remainingFuel
    ) {
      return undefined;
    }

    const unit = input.techlog.aircraft.fuelUnit?.toLowerCase();
    if (unit === 'lb') {
      return summary.postUpliftFuel;
    }
    if (unit === 'kg') {
      return (
        Math.round(summary.postUpliftFuel * POUNDS_PER_KILOGRAM * 100) / 100
      );
    }
    console.warn(
      `Skylegs actual_fuel_start left unset on sector ${input.techlog.sector.id}: TechLog fuel unit "${input.techlog.aircraft.fuelUnit}" on aircraft ${input.techlog.aircraft.registration} is not a mass Skylegs can hold.`,
    );
    return undefined;
  },

  actual_fuel_end: (input: TechlogToSkylegsFlightInput) => {
    const summary = input.techlog.fuelSummary;
    if (!summary) {
      return undefined;
    }

    // Zeros throughout are a sector with no fuel records, not an empty aircraft.
    if (
      !summary.preUpliftFuel &&
      !summary.postUpliftFuel &&
      !summary.usedFuel &&
      !summary.remainingFuel
    ) {
      return undefined;
    }

    const unit = input.techlog.aircraft.fuelUnit?.toLowerCase();
    if (unit === 'lb') {
      return summary.remainingFuel;
    }
    if (unit === 'kg') {
      return (
        Math.round(summary.remainingFuel * POUNDS_PER_KILOGRAM * 100) / 100
      );
    }
    console.warn(
      `Skylegs actual_fuel_end left unset on sector ${input.techlog.sector.id}: TechLog fuel unit "${input.techlog.aircraft.fuelUnit}" on aircraft ${input.techlog.aircraft.registration} is not a mass Skylegs can hold.`,
    );
    return undefined;
  },

  actual_fuel_uplift_before: (input: TechlogToSkylegsFlightInput) => {
    const uplifts = input.techlog.fuelUplifts.filter(
      (uplift) => typeof uplift.aircraftValueActualUplift === 'number',
    );
    if (!uplifts.length) {
      return undefined;
    }

    // Summed from the uplift records rather than derived from the summary:
    // `postUpliftFuel - preUpliftFuel` also carries the departure fuel adjustment.
    // Unlike the fuel on board, a volume uplift converts exactly: the density of
    // the fuel that was pumped is recorded on the record that pumped it.
    const unit = input.techlog.aircraft.fuelUnit?.toLowerCase();
    const masses = uplifts.map((uplift) => {
      const quantity = uplift.aircraftValueActualUplift as number;
      if (unit === 'lb') {
        return quantity;
      }
      if (unit === 'kg') {
        return quantity * POUNDS_PER_KILOGRAM;
      }
      if (
        unit === 'l' &&
        typeof uplift.density === 'number' &&
        uplift.density > 0
      ) {
        return quantity * uplift.density * POUNDS_PER_KILOGRAM;
      }
      return undefined;
    });

    if (masses.some((mass) => mass === undefined)) {
      console.warn(
        `Skylegs actual_fuel_uplift_before left unset on sector ${input.techlog.sector.id}: a TechLog uplift in "${input.techlog.aircraft.fuelUnit}" on aircraft ${input.techlog.aircraft.registration} carries nothing to express it as the pounds Skylegs holds.`,
      );
      return undefined;
    }

    const total = (masses as number[]).reduce((sum, mass) => sum + mass, 0);
    return Math.round(total * 100) / 100;
  },

  fuel_uplift_density_before: (input: TechlogToSkylegsFlightInput) => {
    const uplifts = input.techlog.fuelUplifts.filter(
      (uplift) => typeof uplift.density === 'number' && uplift.density > 0,
    );
    if (!uplifts.length) {
      return undefined;
    }

    // Volume-weighted mean: each record's volume is its mass over its density, so
    // the aggregate is total mass over total volume. The mass unit cancels, which
    // is why no unit reconciliation is needed here.
    const totalMass = uplifts.reduce(
      (sum, uplift) => sum + (uplift.aircraftValueActualUplift ?? 0),
      0,
    );
    const totalVolume = uplifts.reduce(
      (sum, uplift) =>
        sum +
        (uplift.aircraftValueActualUplift ?? 0) / (uplift.density as number),
      0,
    );
    const density =
      totalVolume > 0
        ? totalMass / totalVolume
        : (uplifts[0].density as number);

    // Skylegs enforces 0.680-0.850 and rejects the whole update outside it. A
    // density outside that band is not jet fuel, so the field is dropped rather
    // than losing every other post-flight value with it.
    if (density < 0.68 || density > 0.85) {
      return undefined;
    }

    return Math.round(density * 1000) / 1000;
  },

  saf_before: (input: TechlogToSkylegsFlightInput) => {
    const uplifts = input.techlog.fuelUplifts;
    if (!uplifts.length) {
      return undefined;
    }

    // A blended percentage is only meaningful when every contributing record has
    // a known SAF value; one unknown record would silently dilute the blend.
    if (uplifts.some((uplift) => typeof uplift.safPercentage !== 'number')) {
      return undefined;
    }

    const totalMass = uplifts.reduce(
      (sum, uplift) => sum + (uplift.aircraftValueActualUplift ?? 0),
      0,
    );
    // TechLog stores SAF as a 0-1 fraction; Skylegs takes 0-100.
    const fraction =
      totalMass > 0
        ? uplifts.reduce(
            (sum, uplift) =>
              sum +
              (uplift.aircraftValueActualUplift ?? 0) *
                (uplift.safPercentage as number),
            0,
          ) / totalMass
        : (uplifts[0].safPercentage as number);
    const percentage = fraction * 100;

    if (percentage < 0 || percentage > 100) {
      return undefined;
    }

    return Math.round(percentage * 100) / 100;
  },

  oil_e1: (input: TechlogToSkylegsFlightInput) => {
    if (Number(input.techlog.aircraft.aircraftType?.engineCount) < 1) {
      return undefined;
    }

    // Japat record post-flight oil per engine in a custom field; the standard
    // equivalent is the engine's departure oil level on its fluid uplift record.
    const custom = input.techlog.sector.postflightCustomData?.oil_eng1;
    const engineRecords = input.techlog.fluidUplifts.filter(
      (fluid) =>
        fluid.fluidType === 'oil' && fluid.fluidDestination === 'engine',
    );
    const value =
      typeof custom === 'number'
        ? custom
        : engineRecords.find((fluid) => fluid.fluidDestinationNumber === 1)
            ?.departureValue;
    if (typeof value !== 'number') {
      return undefined;
    }

    const unit = input.techlog.aircraft.oilUnit?.toLowerCase();
    if (unit === 'qt') {
      return value;
    }
    if (unit === 'ml') {
      return Math.round((value / MILLILITRES_PER_QUART) * 100) / 100;
    }
    console.warn(
      `Skylegs oil_e1 left unset on sector ${input.techlog.sector.id}: TechLog oil unit "${input.techlog.aircraft.oilUnit}" on aircraft ${input.techlog.aircraft.registration} is not a quantity Skylegs can hold in quarts.`,
    );
    return undefined;
  },

  oil_e2: (input: TechlogToSkylegsFlightInput) => {
    if (Number(input.techlog.aircraft.aircraftType?.engineCount) < 2) {
      return undefined;
    }

    const custom = input.techlog.sector.postflightCustomData?.oil_eng2;
    const engineRecords = input.techlog.fluidUplifts.filter(
      (fluid) =>
        fluid.fluidType === 'oil' && fluid.fluidDestination === 'engine',
    );
    const value =
      typeof custom === 'number'
        ? custom
        : engineRecords.find((fluid) => fluid.fluidDestinationNumber === 2)
            ?.departureValue;
    if (typeof value !== 'number') {
      return undefined;
    }

    const unit = input.techlog.aircraft.oilUnit?.toLowerCase();
    if (unit === 'qt') {
      return value;
    }
    if (unit === 'ml') {
      return Math.round((value / MILLILITRES_PER_QUART) * 100) / 100;
    }
    console.warn(
      `Skylegs oil_e2 left unset on sector ${input.techlog.sector.id}: TechLog oil unit "${input.techlog.aircraft.oilUnit}" on aircraft ${input.techlog.aircraft.registration} is not a quantity Skylegs can hold in quarts.`,
    );
    return undefined;
  },

  oil_e3: (input: TechlogToSkylegsFlightInput) => {
    if (Number(input.techlog.aircraft.aircraftType?.engineCount) < 3) {
      return undefined;
    }

    const custom = input.techlog.sector.postflightCustomData?.oil_eng3;
    const engineRecords = input.techlog.fluidUplifts.filter(
      (fluid) =>
        fluid.fluidType === 'oil' && fluid.fluidDestination === 'engine',
    );
    const value =
      typeof custom === 'number'
        ? custom
        : engineRecords.find((fluid) => fluid.fluidDestinationNumber === 3)
            ?.departureValue;
    if (typeof value !== 'number') {
      return undefined;
    }

    const unit = input.techlog.aircraft.oilUnit?.toLowerCase();
    if (unit === 'qt') {
      return value;
    }
    if (unit === 'ml') {
      return Math.round((value / MILLILITRES_PER_QUART) * 100) / 100;
    }
    console.warn(
      `Skylegs oil_e3 left unset on sector ${input.techlog.sector.id}: TechLog oil unit "${input.techlog.aircraft.oilUnit}" on aircraft ${input.techlog.aircraft.registration} is not a quantity Skylegs can hold in quarts.`,
    );
    return undefined;
  },

  oil_e4: (input: TechlogToSkylegsFlightInput) => {
    if (Number(input.techlog.aircraft.aircraftType?.engineCount) < 4) {
      return undefined;
    }

    const custom = input.techlog.sector.postflightCustomData?.oil_eng4;
    const engineRecords = input.techlog.fluidUplifts.filter(
      (fluid) =>
        fluid.fluidType === 'oil' && fluid.fluidDestination === 'engine',
    );
    const value =
      typeof custom === 'number'
        ? custom
        : engineRecords.find((fluid) => fluid.fluidDestinationNumber === 4)
            ?.departureValue;
    if (typeof value !== 'number') {
      return undefined;
    }

    const unit = input.techlog.aircraft.oilUnit?.toLowerCase();
    if (unit === 'qt') {
      return value;
    }
    if (unit === 'ml') {
      return Math.round((value / MILLILITRES_PER_QUART) * 100) / 100;
    }
    console.warn(
      `Skylegs oil_e4 left unset on sector ${input.techlog.sector.id}: TechLog oil unit "${input.techlog.aircraft.oilUnit}" on aircraft ${input.techlog.aircraft.registration} is not a quantity Skylegs can hold in quarts.`,
    );
    return undefined;
  },

  oil_apu: (input: TechlogToSkylegsFlightInput) => {
    const value = input.techlog.fluidUplifts.find(
      (fluid) => fluid.fluidType === 'oil' && fluid.fluidDestination === 'apu',
    )?.departureValue;
    if (typeof value !== 'number') {
      return undefined;
    }

    const unit = input.techlog.aircraft.oilUnit?.toLowerCase();
    if (unit === 'qt') {
      return value;
    }
    if (unit === 'ml') {
      return Math.round((value / MILLILITRES_PER_QUART) * 100) / 100;
    }
    console.warn(
      `Skylegs oil_apu left unset on sector ${input.techlog.sector.id}: TechLog oil unit "${input.techlog.aircraft.oilUnit}" on aircraft ${input.techlog.aircraft.registration} is not a quantity Skylegs can hold in quarts.`,
    );
    return undefined;
  },

  oil_uplift_e1: (input: TechlogToSkylegsFlightInput) => {
    if (Number(input.techlog.aircraft.aircraftType?.engineCount) < 1) {
      return undefined;
    }

    const engineRecords = input.techlog.fluidUplifts.filter(
      (fluid) =>
        fluid.fluidType === 'oil' &&
        fluid.fluidDestination === 'engine' &&
        typeof fluid.uplift === 'number',
    );
    const uplifts = engineRecords.filter(
      (fluid) => fluid.fluidDestinationNumber === 1,
    );
    if (!uplifts.length) {
      // These slots are the only oil boxes the Skylegs post-flight form renders,
      // so an operator who records oil in the post-flight custom field sees
      // nothing at all unless that figure lands here.
      const custom = input.techlog.sector.postflightCustomData?.oil_eng1;
      if (typeof custom === 'number') {
        const customUnit = input.techlog.aircraft.oilUnit?.toLowerCase();
        if (customUnit === 'qt') {
          return custom;
        }
        if (customUnit === 'ml') {
          return Math.round((custom / MILLILITRES_PER_QUART) * 100) / 100;
        }
        return undefined;
      }
      return undefined;
    }

    // Each record carries its own unit, and TechLog holds oil uplifts in only
    // millilitres or quarts, so every record converts exactly onto the quarts
    // Skylegs holds.
    return (
      Math.round(
        uplifts.reduce(
          (sum, fluid) =>
            sum +
            (fluid.upliftUnit === 'ml'
              ? (fluid.uplift as number) / MILLILITRES_PER_QUART
              : (fluid.uplift as number)),
          0,
        ) * 100,
      ) / 100
    );
  },

  oil_uplift_e2: (input: TechlogToSkylegsFlightInput) => {
    if (Number(input.techlog.aircraft.aircraftType?.engineCount) < 2) {
      return undefined;
    }

    const engineRecords = input.techlog.fluidUplifts.filter(
      (fluid) =>
        fluid.fluidType === 'oil' &&
        fluid.fluidDestination === 'engine' &&
        typeof fluid.uplift === 'number',
    );
    const uplifts = engineRecords.filter(
      (fluid) => fluid.fluidDestinationNumber === 2,
    );
    if (!uplifts.length) {
      const custom = input.techlog.sector.postflightCustomData?.oil_eng2;
      if (typeof custom === 'number') {
        const customUnit = input.techlog.aircraft.oilUnit?.toLowerCase();
        if (customUnit === 'qt') {
          return custom;
        }
        if (customUnit === 'ml') {
          return Math.round((custom / MILLILITRES_PER_QUART) * 100) / 100;
        }
        return undefined;
      }
      return undefined;
    }

    return (
      Math.round(
        uplifts.reduce(
          (sum, fluid) =>
            sum +
            (fluid.upliftUnit === 'ml'
              ? (fluid.uplift as number) / MILLILITRES_PER_QUART
              : (fluid.uplift as number)),
          0,
        ) * 100,
      ) / 100
    );
  },

  oil_uplift_e3: (input: TechlogToSkylegsFlightInput) => {
    if (Number(input.techlog.aircraft.aircraftType?.engineCount) < 3) {
      return undefined;
    }

    const engineRecords = input.techlog.fluidUplifts.filter(
      (fluid) =>
        fluid.fluidType === 'oil' &&
        fluid.fluidDestination === 'engine' &&
        typeof fluid.uplift === 'number',
    );
    const uplifts = engineRecords.filter(
      (fluid) => fluid.fluidDestinationNumber === 3,
    );
    if (!uplifts.length) {
      const custom = input.techlog.sector.postflightCustomData?.oil_eng3;
      if (typeof custom === 'number') {
        const customUnit = input.techlog.aircraft.oilUnit?.toLowerCase();
        if (customUnit === 'qt') {
          return custom;
        }
        if (customUnit === 'ml') {
          return Math.round((custom / MILLILITRES_PER_QUART) * 100) / 100;
        }
        return undefined;
      }
      return undefined;
    }

    return (
      Math.round(
        uplifts.reduce(
          (sum, fluid) =>
            sum +
            (fluid.upliftUnit === 'ml'
              ? (fluid.uplift as number) / MILLILITRES_PER_QUART
              : (fluid.uplift as number)),
          0,
        ) * 100,
      ) / 100
    );
  },

  oil_uplift_e4: (input: TechlogToSkylegsFlightInput) => {
    if (Number(input.techlog.aircraft.aircraftType?.engineCount) < 4) {
      return undefined;
    }

    const engineRecords = input.techlog.fluidUplifts.filter(
      (fluid) =>
        fluid.fluidType === 'oil' &&
        fluid.fluidDestination === 'engine' &&
        typeof fluid.uplift === 'number',
    );
    const uplifts = engineRecords.filter(
      (fluid) => fluid.fluidDestinationNumber === 4,
    );
    if (!uplifts.length) {
      const custom = input.techlog.sector.postflightCustomData?.oil_eng4;
      if (typeof custom === 'number') {
        const customUnit = input.techlog.aircraft.oilUnit?.toLowerCase();
        if (customUnit === 'qt') {
          return custom;
        }
        if (customUnit === 'ml') {
          return Math.round((custom / MILLILITRES_PER_QUART) * 100) / 100;
        }
        return undefined;
      }
      return undefined;
    }

    return (
      Math.round(
        uplifts.reduce(
          (sum, fluid) =>
            sum +
            (fluid.upliftUnit === 'ml'
              ? (fluid.uplift as number) / MILLILITRES_PER_QUART
              : (fluid.uplift as number)),
          0,
        ) * 100,
      ) / 100
    );
  },

  oil_uplift_apu: (input: TechlogToSkylegsFlightInput) => {
    const uplifts = input.techlog.fluidUplifts.filter(
      (fluid) =>
        fluid.fluidType === 'oil' &&
        fluid.fluidDestination === 'apu' &&
        typeof fluid.uplift === 'number',
    );
    if (!uplifts.length) {
      return undefined;
    }

    return (
      Math.round(
        uplifts.reduce(
          (sum, fluid) =>
            sum +
            (fluid.upliftUnit === 'ml'
              ? (fluid.uplift as number) / MILLILITRES_PER_QUART
              : (fluid.uplift as number)),
          0,
        ) * 100,
      ) / 100
    );
  },

  deicing_count: (input: TechlogToSkylegsFlightInput) =>
    input.techlog.deiceEvents.length > 0
      ? input.techlog.deiceEvents.length
      : undefined,

  deicing_type: (input: TechlogToSkylegsFlightInput) => {
    const types = [
      ...new Set(
        input.techlog.deiceEvents
          .map((event) => event.fluidType?.trim().toUpperCase())
          .filter((type): type is string => !!type),
      ),
    ];

    // Skylegs holds one type per flight and accepts only "1"-"4". Several types
    // on one sector cannot be represented, and TechLog's V and E have no
    // destination code at all, so both cases are left unset rather than
    // flattened onto a type the crew did not apply.
    if (types.length !== 1) {
      return undefined;
    }

    const codeByTechlogType: Record<string, '1' | '2' | '3' | '4'> = {
      I: '1',
      II: '2',
      III: '3',
      IV: '4',
    };

    return codeByTechlogType[types[0]];
  },

  deicing_time: (input: TechlogToSkylegsFlightInput) => {
    // Skylegs holds the de-ice clock as a time of day in seconds since midnight
    // — tenant flights carry 59400 for 16:30 — so what transfers is the wall
    // clock of the earliest application, not how long it took. TechLog does not
    // honour its ISO contract on these fields (`YYYY/MM/DD hh:mm AM` and a bare
    // `HH:mm` both occur), so the hours and minutes are read off the recorded
    // string rather than parsed as a date.
    const secondsOfDay = input.techlog.deiceEvents
      .map((event) => {
        const recorded = /(\d{1,2}):(\d{2})(?:\s*([AaPp])[Mm.]*)?/.exec(
          event.startTime ?? '',
        );
        if (!recorded) {
          return null;
        }

        let hours = Number(recorded[1]);
        const minutes = Number(recorded[2]);
        const meridiem = recorded[3]?.toLowerCase();
        if (meridiem === 'p' && hours < 12) {
          hours += 12;
        }
        if (meridiem === 'a' && hours === 12) {
          hours = 0;
        }
        if (hours > 23 || minutes > 59) {
          return null;
        }

        return hours * 3600 + minutes * 60;
      })
      .filter((seconds): seconds is number => seconds !== null);

    if (!secondsOfDay.length) {
      return undefined;
    }

    return Math.min(...secondsOfDay);
  },

  // Both systems hold the quantity in litres, so the applications sum with no
  // conversion.
  deicing_amount: (input: TechlogToSkylegsFlightInput) => {
    const quantities = input.techlog.deiceEvents
      .map((event) => event.quantity)
      .filter((quantity): quantity is number => typeof quantity === 'number');

    if (!quantities.length) {
      return undefined;
    }

    return (
      Math.round(
        quantities.reduce((sum, quantity) => sum + quantity, 0) * 100,
      ) / 100
    );
  },

  deicing_percentage: (input: TechlogToSkylegsFlightInput) => {
    // TechLog records the mixture as fluid against water — entering 40 renders
    // as 40/60 — so the left ratio is the fluid concentration Skylegs asks for.
    // Two applications blend by volume, the same way an uplift density does:
    // what reached the airframe is total fluid over total mixture.
    const applications = input.techlog.deiceEvents.filter(
      (event) => typeof event.fluidRatioLeft === 'number',
    );
    if (!applications.length) {
      return undefined;
    }

    const totalVolume = applications.reduce(
      (sum, event) => sum + (event.quantity ?? 0),
      0,
    );
    const ratio =
      totalVolume > 0
        ? applications.reduce(
            (sum, event) =>
              sum + (event.quantity ?? 0) * (event.fluidRatioLeft as number),
            0,
          ) / totalVolume
        : applications.reduce(
            (sum, event) => sum + (event.fluidRatioLeft as number),
            0,
          ) / applications.length;

    const percentage = Math.round(ratio);
    if (percentage < 0 || percentage > 100) {
      return undefined;
    }

    return percentage;
  },

  crew_string: (input: TechlogToSkylegsFlightInput) => {
    // Position carries the seat: the first code is the left seat (pilot in
    // command), the second the right. The list is therefore built by seat and
    // never compacted — a second in command sent on its own would arrive in
    // Skylegs as the pilot in command of the flight.
    const pilotsBySeat = [
      input.techlog.pilotInCommand,
      input.techlog.secondInCommand,
    ];

    // Without a left seat there is no list to send, whoever else the sector
    // records.
    if (!pilotsBySeat[0]) {
      return undefined;
    }

    const codes = pilotsBySeat
      .filter((pilot): pilot is NonNullable<typeof pilot> => !!pilot)
      .map((pilot) => {
        // The seat code lives on the organisation membership, and the list
        // carries memberships that are no longer active.
        const membership = input.skylegs.organisationUsers.find(
          (user) =>
            user.pivot.status === 'active' &&
            user.pivot.email?.toLowerCase() === pilot.email.toLowerCase(),
        );
        return membership?.pivot.code ?? undefined;
      });

    // A pilot the sector records but Skylegs has no active membership for has no
    // code, and dropping it would move the remaining pilot into the wrong seat,
    // so the flight's existing crew assignment is left alone instead.
    if (codes.some((code) => !code)) {
      return undefined;
    }

    // The value is the flight's whole crew, not just the seats TechLog fills:
    // Skylegs sets the crew to exactly this list, so anyone standing behind the
    // flight deck has to be carried across or they come off the flight. Skylegs
    // reads the list space-separated and takes it comma-separated.
    const crewBehindTheFlightDeck = (input.skylegs.flight?.crew_string ?? '')
      .split(/[\s,]+/)
      .filter(Boolean)
      .slice(pilotsBySeat.length);

    if (!crewBehindTheFlightDeck.length) {
      return codes.join(',');
    }

    // The list has no empty slot, so a sector with one pilot cannot be expressed
    // alongside the rest of the crew: the first of them would take the right
    // seat. The crew is left as it stands rather than seating a cabin crew
    // member as second in command.
    if (codes.length < pilotsBySeat.length) {
      return undefined;
    }

    // Skylegs resolves every code in the list against the device's active
    // organisation and rejects the whole update naming the ones it does not
    // know, which would cost the sector's times, fuel, oil and counters too.
    const isAssignable = (code: string) =>
      input.skylegs.organisationUsers.some(
        (user) => user.pivot.status === 'active' && user.pivot.code === code,
      );
    if (!crewBehindTheFlightDeck.every(isAssignable)) {
      return undefined;
    }

    return [...codes, ...crewBehindTheFlightDeck].join(',');
  },
};

export default techlogFlightToSkylegsSchema;
